<script>
import Paginator from "@/javascript/views/components/Pagination.vue";
import {useRoute, useRouter} from "vue-router";
import {useDonationStore} from "@/javascript/stores/donation";
import {ApplicationHelper} from "../../../helpers/ApplicationHelper";
import {useI18n} from "vue-i18n";
import {useOrganizationStore} from "../../../stores/organizations";
import Popup from "@/javascript/views/components/popup.vue";
import {useSubscriptionStore} from "../../../stores/subscription";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import LoadingSpinner from "../../components/loadingSpinner.vue";
import TabsComponent from "@/javascript/views/components/dashboard/Tabs.vue";
import BaseIndex from "../../components/dashboard/Index.vue";
import ExcelImporter from "../../components/excelImport/ExcelImporter.vue";
import DonationReportExporter from "@/javascript/views/clientDashboard/donations/DonationReportExporter.vue";
import BaseModal from "@/javascript/views/components/BaseModal.vue";


export default {
  name: "DonationsIndex",
  components: {
    DonationReportExporter,
    ExcelImporter,
    BaseIndex,
    LoadingSpinner, Popup, Paginator, TabsComponent, BaseModal},
  setup() {
    // Router
    const route = useRoute();
    const router = useRouter();

    // i18n
    const {t} = useI18n();
    const donationStore = useDonationStore();
    const subscriptionStore = useSubscriptionStore();
    const organizationStore = useOrganizationStore();
    const analyticEventStore = useAnalyticEventStore();

    return {
      router,
      route,
      t,
      donationStore,
      subscriptionStore,
      organizationStore,
      analyticEventStore
    };
  },

  data() {
    return {
      showOfflineModal: false,
      showGiftTemplatesModal: false,
      thankYouMessage: "",
      savingThankYou: false,
      placeholders: [
        { token: "[Organization]", label: "Organization" },
        { token: "[Donor]", label: "Donor" },
        { token: "[Amount]", label: "Amount" },
      ],
      activeTab: 'donations-tab',
      previousTab: null,
      popupMessage: "",
      reportLoading: false
    };
  },
  computed: {
    ApplicationHelper() {
      return ApplicationHelper;
    },
    loading() {
      return this.donationStore.loading;
    },
    canCreate() {
      return this.donationStore.permissions && this.donationStore.permissions.offline_donation;
    },
  },
  mounted() {
    if (this.route.params.panel === 'recurring-gifts') {
      this.activeTab = 'subscriptions-tab';
    }
    this.organizationStore.getOrganization();
  },

  methods: {
    showPopup(message) {
      this.popupMessage = message;
      this.$refs.popup.show();

    },
    switchTab(tab) {
      this.previousTab = this.activeTab;
      this.activeTab = tab;
      this.router.replace({
        name: "client-dashboard",
        params: {
          ...this.route.params,
          panel: tab === 'donations-tab' ? 'gifts' : 'recurring-gifts',
        }
      });
      this.analyticEventStore.logEvent({
        action: `donations-index-switch-to-${tab}`,
      });
    },

    openOfflineModal() {
      this.donationStore.toggleOfflineModal();
      this.analyticEventStore.logEvent({
        action: `offline-gift-open-offline-modal`,
      });
    },

    openGiftTemplatesModal() {
      this.thankYouMessage = this.organizationStore.organization.thank_you_message || "";
      this.showGiftTemplatesModal = true;
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'donation-email-template-open' });
    },

    insertPlaceholder(token) {
      const textarea = this.$refs.thankYouTextarea;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      this.thankYouMessage =
        this.thankYouMessage.substring(0, start) + token + this.thankYouMessage.substring(end);
      this.$nextTick(() => {
        const pos = start + token.length;
        textarea.focus();
        textarea.setSelectionRange(pos, pos);
      });
    },

    async saveThankYouMessage() {
      this.savingThankYou = true;
      try {
        await this.organizationStore.organizationService.update(
          this.organizationStore.organization.id,
          { thank_you_message: this.thankYouMessage }
        );
        this.organizationStore.organization.thank_you_message = this.thankYouMessage;
        this.showGiftTemplatesModal = false;
        this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
        this.analyticEventStore.logEvent({ action: 'donation-email-template-saved' });
        this.showPopup("Thank you message updated successfully");
      } catch (e) {
        console.error("Failed to update thank you message", e);
      }
      this.savingThankYou = false;
    },

    beforeEnter(el) {
      el.style.transform = `translateX(${this.previousTab === 'donations-tab' && this.activeTab === 'subscriptions-tab' ? '100%' : '-100%'})`;
    },

    enter(el, done) {
      // Trigger reflow
      el.offsetWidth; // eslint-disable-line  @typescript-eslint/no-unused-expressions
      el.style.transition = 'transform 0.3s ease-in-out';
      el.style.transform = 'translateX(0)';
      done();
    },

    leave(el, done) {
      el.style.transition = 'transform 0.3s ease-in-out';
      el.style.transform = `translateX(${this.previousTab === 'donations-tab' && this.activeTab === 'subscriptions-tab' ? '-100%' : '100%'})`;
      done();
    }
  }
};
</script>

<template>
  <div class="donations-index-wrapper w-full p-4 pt-2">
    <div class="bg-white rounded-xl border border-gray-300 p-4 px-6 flex justify-between items-center">
      <p class="pb-0 font-bold">
        Gifts
      </p>
      <div class="flex flex-row text-sm">
        <button
          v-if="canCreate"
          class="btn-outline mr-2"
          @click="openGiftTemplatesModal"
        >
          Email Template
        </button>
        <button
          v-if="canCreate"
          class="btn-outline mr-2"
          @click="openOfflineModal"
        >
          Offline Gift
        </button>
      </div>
    </div>

    <!-- Tab Component -->
    <TabsComponent
      v-model="activeTab"
      :tabs="[
        { id: 'donations-tab', label: 'One Time Gifts' },
        { id: 'subscriptions-tab', label: 'Recurring Gifts' }
      ]"
      class="mt-4 border border-gray-300 rounded-xl overflow-hidden text-xs"
      @tab-changed="switchTab"
    >
      <!-- One Time Gifts Tab -->
      <template #donations-tab>
        <div class="tab-pane">
          <base-index :model-store="donationStore" />
        </div>
      </template>

      <!-- Recurring Gifts Tab -->
      <template #subscriptions-tab>
        <div class="tab-pane">
          <base-index :model-store="subscriptionStore" />
        </div>
      </template>
    </TabsComponent>

    <!--    &lt;!&ndash; Modals &ndash;&gt;-->
    <!--    <offline-donation-modal-->
    <!--      v-if="donationStore.showOfflineModal"-->
    <!--    />-->

    <excel-importer
      v-if="donationStore.showOfflineModal && donationStore.targetsLoaded"
      :model-store="donationStore"
      @close="donationStore.toggleOfflineModal"
    />

    <BaseModal
      v-if="showGiftTemplatesModal"
      title="Gift Email Templates"
      size="small"
      @close="showGiftTemplatesModal = false"
    >
      <div class="border-t border-gray-300 mb-4" />
      <label
        for="thank_you_message"
        class="text-sm font-semibold"
      >
        Thank You Message
      </label>
      <p class="text-xs text-gray-500 mt-1">
        Insert dynamic values that will be replaced in the email:
      </p>
      <div class="flex flex-wrap gap-1 mt-2">
        <button
          v-for="placeholder in placeholders"
          :key="placeholder.token"
          type="button"
          class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-default_secondary_dark text-white hover:bg-default_secondary transition-colors cursor-pointer"
          @click="insertPlaceholder(placeholder.token)"
        >
          {{ placeholder.label }}
        </button>
      </div>
      <textarea
        id="thank_you_message"
        ref="thankYouTextarea"
        v-model="thankYouMessage"
        rows="4"
        placeholder="Your contributions help us continue our mission to make a meaningful difference in the lives of others."
        class="mt-2 w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
      />
      <div class="flex justify-end space-x-2 mt-4">
        <button
          class="btn-outline"
          @click="showGiftTemplatesModal = false"
        >
          Cancel
        </button>
        <button
          class="btn-filled"
          :disabled="savingThankYou"
          @click="saveThankYouMessage"
        >
          {{ savingThankYou ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </BaseModal>

    <popup
      ref="popup"
      :message="popupMessage"
      :duration="1400"
    />
  </div>
</template>

<style lang="scss">
.donations-index-wrapper {
  th {
    font-size: 10px;
    font-weight: normal;
    text-align: left;
    text-transform: uppercase;
    @apply text-slate-500;
    @apply px-2;
    @apply py-2;
  }

  td {
    font-size: 12px;
    @apply px-2;
    @apply py-4;
  }

  //tr:hover {
  //  color: darkcyan;
  //}

  //.tab-pane {
  //  background-color: white;
  //}

  .slide-enter-active, .slide-leave-active {
    transition: transform 0.3s ease-in-out;
  }

  .slide-enter, .slide-leave-to {
    transform: translateX(100%);
  }

  .slide-leave {
    transform: translateX(-100%);
  }

  @media screen and (max-width: 720px) {
    .tab-container {
      flex-direction: row;
      align-items: center;
    }
  }
}

.report-button.disabled {
  &:hover {
    background-color: white !important;
    cursor: wait;
  }
}
</style>
